/**
 * LanguageContext
 *
 * Provides language state and switching functionality throughout the app.
 * Integrates with i18next for translation management.
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();

  // Normalize language code to 'en', 'zh-CN', or 'zh-TW'
  const normalizeLanguage = (lng) => {
    if (!lng) return "en";

    // Handle Traditional Chinese variants
    if (lng === "zh-TW" || lng === "zh-Hant" || lng === "zh-HK") {
      return "zh-TW";
    }

    // Handle Simplified Chinese variants
    if (
      lng === "zh" ||
      lng === "zh-CN" ||
      lng === "zh-Hans" ||
      lng.startsWith("zh")
    ) {
      return "zh-CN";
    }

    // Handle English variants (en-US, en-GB, etc.)
    if (lng.startsWith("en")) {
      return "en";
    }

    // Default to English for unsupported languages
    return "en";
  };

  const [currentLanguage, setCurrentLanguage] = useState(() =>
    normalizeLanguage(i18n.language),
  );

  useEffect(() => {
    // Normalize the initial language on mount
    const normalized = normalizeLanguage(i18n.language);
    if (i18n.language !== normalized) {
      i18n.changeLanguage(normalized);
    }

    // Update state when language changes
    const handleLanguageChange = (lng) => {
      const normalizedLng = normalizeLanguage(lng);
      setCurrentLanguage(normalizedLng);

      // Ensure i18n uses the normalized language
      if (lng !== normalizedLng) {
        i18n.changeLanguage(normalizedLng);
      }
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n]);

  /**
   * Change the current language
   * @param {string} lng - Language code ('en' or 'zh-CN')
   */
  const changeLanguage = async (lng) => {
    try {
      await i18n.changeLanguage(lng);
    } catch (error) {
      console.error("Error changing language:", error);
    }
  };

  /**
   * Toggle between English and Chinese (for backward compatibility)
   */
  const toggleLanguage = () => {
    const newLang = currentLanguage === "en" ? "zh-CN" : "en";
    changeLanguage(newLang);
  };

  /**
   * Select a specific language
   * @param {string} lng - Language code ('en', 'zh-CN', or 'zh-TW')
   */
  const selectLanguage = (lng) => {
    changeLanguage(lng);
  };

  /**
   * Get display name for current language
   */
  const getLanguageDisplayName = (lng = currentLanguage) => {
    // Normalize the language first
    const normalizedLng = normalizeLanguage(lng);

    const displayNames = {
      en: "EN",
      "zh-CN": "简中",
      "zh-TW": "繁中",
    };

    return displayNames[normalizedLng] || "EN";
  };

  /**
   * Check if current language is Chinese (Simplified or Traditional)
   */
  const isChinese = () => {
    return currentLanguage === "zh-CN" || currentLanguage === "zh-TW";
  };

  /**
   * Check if current language is Simplified Chinese
   */
  const isSimplifiedChinese = () => {
    return currentLanguage === "zh-CN";
  };

  /**
   * Check if current language is Traditional Chinese
   */
  const isTraditionalChinese = () => {
    return currentLanguage === "zh-TW";
  };

  /**
   * Check if current language is English
   */
  const isEnglish = () => {
    return currentLanguage === "en";
  };

  const value = {
    currentLanguage,
    changeLanguage,
    toggleLanguage,
    selectLanguage,
    getLanguageDisplayName,
    isChinese,
    isSimplifiedChinese,
    isTraditionalChinese,
    isEnglish,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
